import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import MemberPicker from "@/components/MemberPicker";

const Section = ({ title, hint, children }) => (
  <div className="space-y-2 rounded-xl border border-zinc-800 p-4">
    <div>
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-xs text-zinc-500">{hint}</div>
    </div>
    {children}
  </div>
);

export default function JoinerFormDialog({ joiner, onClose, onSaved }) {
  const isEdit = !!joiner.id;
  const [f, setF] = useState({
    name: joiner.name || "",
    gom_claims: joiner.gom_claims || [],
    fixed_claims: joiner.fixed_claims || [],
    soft_claims: joiner.soft_claims || [],
    open_claims: joiner.open_claims || [],
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, handle: joiner.handle || "", contact: joiner.contact || "", notes: joiner.notes || "" };
      if (isEdit) await api.put(`/joiners/${joiner.id}`, body);
      else await api.post("/joiners", body);
      toast.success(isEdit ? "Joiner updated" : "Joiner added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't save joiner"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="joiner-form-dialog" className="bg-zinc-950 border-zinc-800 sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit joiner" : "Add joiner"}</DialogTitle>
          <DialogDescription>Claims fill in priority order: OT8 (a whole set) first, then GoM, Fixed, Soft and Open. Within each, first come, first served.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Joiner Name</Label>
            <Input data-testid="joiner-name-input" required value={f.name} onChange={(e) => set("name")(e.target.value)} className="bg-zinc-900" />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold pt-1">Member Claims</div>
          <Section title="(First Priority) GoM Claims" hint="Always filled first">
            <MemberPicker value={f.gom_claims} onChange={set("gom_claims")} testPrefix="joiner-gom" tone="amber" />
          </Section>
          <Section title="(Second Priority) Fixed Claims" hint="Pre-selected whenever this joiner picks their name on a drop">
            <MemberPicker value={f.fixed_claims} onChange={set("fixed_claims")} testPrefix="joiner-fixed" tone="pink" />
          </Section>
          <Section title="(Third Priority) Soft Claims" hint="Also pre-selected when they pick their name on a drop">
            <MemberPicker value={f.soft_claims} onChange={set("soft_claims")} testPrefix="joiner-soft" tone="sky" />
          </Section>
          <Section title="(Last Priority) Open Claims" hint="Shown as suggestions; the joiner can pick these on the drop page">
            <MemberPicker value={f.open_claims} onChange={set("open_claims")} testPrefix="joiner-open" tone="zinc" />
          </Section>
          <Button type="submit" disabled={busy} data-testid="joiner-save-button" className="w-full h-11 rounded-xl bg-white text-zinc-950 hover:bg-zinc-200 font-semibold">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isEdit ? "Save changes" : "Add joiner"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
